import { useEffect, useRef } from "react";
import type { HierarchicalItem } from "../../../types/uiTypes";

type UseUrlSyncProps<T> = {
  searchParams: URLSearchParams;
  modal: { type: "create" | "edit"; item?: T; parentItem?: T } | null;
  data: T[] | undefined;
  findItemById: (itemList: T[], id: string) => T | undefined;
  treeData: T[];
  openEditModal: (item: T, updateUrl?: boolean) => void;
  setModal: (modal: { type: "create" | "edit"; item?: T; parentItem?: T } | null) => void;
  setSearchParams: (params: Record<string, string>) => void;
};

export function useUrlSync<T extends HierarchicalItem>({
  searchParams,
  modal,
  data,
  findItemById,
  treeData,
  openEditModal,
  setModal,
  setSearchParams,
}: UseUrlSyncProps<T>) {
  // Track if we're programmatically updating the URL (to prevent sync loop)
  const isUpdatingUrlRef = useRef(false);

  useEffect(() => {
    // If we're programmatically updating the URL, skip this sync
    if (isUpdatingUrlRef.current) {
      isUpdatingUrlRef.current = false;
      return;
    }

    const editId = searchParams.get("edit");
    const currentEditId = modal?.type === "edit" && modal.item ? modal.item.id : null;

    // Only sync if the URL edit ID doesn't match the current modal
    if (editId && editId !== currentEditId && data) {
      const item = findItemById(treeData, editId);
      if (item) {
        openEditModal(item, false);
      } else {
        // Item not found, clear the param
        setSearchParams({});
      }
    } else if (!editId && modal?.type === "edit") {
      // URL param was cleared (e.g., back button), close modal
      setModal(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, data]);

  return {
    markUrlUpdating: () => {
      isUpdatingUrlRef.current = true;
    },
  };
}
